import type { ReactNode } from 'react'
import { fbt } from 'fbtee'
import { ChevronDown } from 'lucide-react'
import { goingSectionHeading } from '../../lib/commonStrings'
import { useOpenEvent } from '../../hooks/useOpenEvent'
import { useIncrementalReveal } from '../../hooks/useIncrementalReveal'
import { useVisitState } from '../../hooks/useTabVisit'
import { placeholders } from '../../lib/skeletonUtils'
import { cn } from '../../lib/utils'
import { EventCard } from '../EventCard'
import { Section } from '../Section'
import { EmptyState } from '../EmptyState'
import type { Event, UserProfile } from '../../types'
import type { EventInterestStatus } from '../../hooks/useUserEventInterests'
import type { FacepileBucket } from '../../hooks/useFacepileData'
import { SocialEventCard } from '../SocialEventCard'

/**
 * A profile's Events tab, the same for your own and someone else's: what
 * they're going to, their weaker "interested" picks folded behind one line,
 * and further down the past events they went to.
 */
export function ProfileEventsView({
  goingEvents,
  interestedEvents,
  pastEvents,
  myStatuses,
  onSetInterest,
  facepileData,
  profiles,
  currentUserId,
  loading,
  locale,
  isSelf = false,
  empty,
}: {
  goingEvents: Event[]
  interestedEvents: Event[]
  /** Past events they went to, latest first. */
  pastEvents: Event[]
  myStatuses: Map<string, EventInterestStatus>
  /** The viewer's RSVP control on each card; omitted on your own profile. */
  onSetInterest?: (eventId: string, status: EventInterestStatus) => void
  facepileData: Map<string, FacepileBucket>
  profiles: Record<string, UserProfile>
  currentUserId: string | null
  loading: boolean
  locale: string
  isSelf?: boolean
  /** Replaces the default Going empty state (e.g. a sign-up upsell for guests). */
  empty?: ReactNode
}) {
  const openEvent = useOpenEvent()
  const [showInterested, setShowInterested] = useVisitState(false)

  const renderCard = (event: Event) => (
    <SocialEventCard
      key={event.id}
      event={event}
      locale={locale}
      facepile={facepileData.get(event.id)}
      profiles={profiles}
      currentUserId={currentUserId}
      status={myStatuses.get(event.id) ?? 'none'}
      onOpen={() => openEvent(event, myStatuses.get(event.id))}
      onSetStatus={onSetInterest && (next => onSetInterest(event.id, next))}
    />
  )

  const emptyGoing = empty ?? (
    <EmptyState>
      {isSelf
        ? fbt(
            'Festivals and parties you’re going to will show up here.',
            'Empty state for the Going section on the own profile when there are no upcoming plans',
          )
        : fbt('Not going to any events yet', 'Empty state for the Going section on a user profile')}
    </EmptyState>
  )

  return (
    <>
      <Section title={goingSectionHeading()}>
        {loading ? (
          <div className="space-y-3">
            {placeholders(2).map((_, i) => (
              <EventCard key={i} loading />
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {goingEvents.length === 0 ? emptyGoing : goingEvents.map(renderCard)}
            {interestedEvents.length > 0 && (
              <button
                type="button"
                onClick={() => setShowInterested(v => !v)}
                aria-expanded={showInterested}
                className="flex cursor-pointer items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
                data-testid="profile-interested-toggle"
              >
                <fbt desc="Collapsed line on someone's profile revealing the events they're only interested in">
                  Also interested in{' '}
                  <fbt:plural count={interestedEvents.length} showCount="yes" many="events">
                    event
                  </fbt:plural>
                </fbt>
                <ChevronDown className={cn('size-4 transition-transform', showInterested && 'rotate-180')} />
              </button>
            )}
            {showInterested && interestedEvents.map(renderCard)}
          </div>
        )}
      </Section>
      {!loading && pastEvents.length > 0 && <PastEventsSection events={pastEvents} renderCard={renderCard} />}
    </>
  )
}

function PastEventsSection({ events, renderCard }: { events: Event[]; renderCard: (event: Event) => ReactNode }) {
  const { count, sentinelRef } = useIncrementalReveal(events.length)
  return (
    <Section title={fbt('Been to', 'Section heading on the own profile for past events the user went to')}>
      <div className="space-y-3" data-testid="profile-past-events">
        {events.slice(0, count).map(renderCard)}
        <div ref={sentinelRef} />
      </div>
    </Section>
  )
}
